/**
 * ObrasTablero — tablero principal de Obras PVC.
 *
 * Muestra todas las obras con su estado actual, filtros por estado y
 * alertas de estancamiento (obra que lleva demasiados días en un estado).
 * Reemplaza la coordinación por WhatsApp: cada uno entra y ve su cola.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { listenObras, crearObra, subirArchivoObra, getClientesLista, agregarCobranza, adjuntarDocCobranza } from "../services/obrasFS";
import {
  FASES_OBRA, CARPINTEROS, faseObra, hitosObra, obraConGuale, fechaFaseActual,
  accionesPendientesPara, permisosObras, progresoObra, responsableActual,
} from "../services/estadosObra";
import CostosModal from "../components/CostosModal";
import ProductosModal from "../components/ProductosModal";
import ClientesModal from "../components/ClientesModal";

// ─────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────

function toDate(ts) {
  if (!ts) return null;
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  const d = ts instanceof Date ? ts : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

/** Días (enteros) que la obra lleva en su fase actual */
function diasEnFase(obra) {
  const d = fechaFaseActual(obra) || toDate(obra.updatedAt) || toDate(obra.creadoAt);
  if (!d) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function estaAtrasada(obra) {
  const fase = faseObra(obra);
  if (!fase?.diasAlerta) return false;
  const dias = diasEnFase(obra);
  return dias != null && dias > fase.diasAlerta;
}

/** Barra de progreso por hitos de la obra (verde hecho · azul actual · gris pendiente) */
export function ProgresoBar({ obra, alto = 6 }) {
  const { hitos, orden } = progresoObra(obra);
  let actualMarcado = false;
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {orden.map(k => {
        const ok = hitos[k];
        let bg = "#e2e8f0";
        if (ok) bg = "#4ade80";
        else if (!actualMarcado) { bg = "#60a5fa"; actualMarcado = true; }
        return <span key={k} style={{ flex: 1, height: alto, borderRadius: 2, background: bg, minWidth: 10 }} />;
      })}
    </div>
  );
}

/** Avatar con inicial del responsable actual del proceso */
function AvatarResp({ resp }) {
  const nombre = resp?.nombre;
  if (!resp || !nombre) return <span style={{ color: "#94a3b8" }}>—</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
      <span style={{
        width: 20, height: 20, borderRadius: "50%", background: resp.bg, color: resp.color,
        fontSize: 10.5, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        {nombre[0]}
      </span>
      <span style={{ fontSize: 12, color: "#475569" }}>{nombre}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
//  Tarjeta de obra
// ─────────────────────────────────────────────────────────────

function ObraCard({ obra, meToca }) {
  const navigate = useNavigate();
  const cfg = faseObra(obra);
  const dias = diasEnFase(obra);
  const atrasada = estaAtrasada(obra);
  const resp = responsableActual(obra);
  const { completos, total } = progresoObra(obra);

  return (
    <div
      onClick={() => navigate(`/obras/${encodeURIComponent(obra.id)}`)}
      style={{
        background: "#fff",
        border: atrasada ? "2px solid #f97316" : "1px solid #e2e8f0",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        boxShadow: atrasada ? "0 3px 10px rgba(249,115,22,0.18)" : "0 2px 6px rgba(15,23,42,0.06)",
        transition: "transform 0.1s, box-shadow 0.1s",
        display: "flex", flexDirection: "column", gap: 8,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,23,42,0.12)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = atrasada ? "0 3px 10px rgba(249,115,22,0.18)" : "0 2px 6px rgba(15,23,42,0.06)"; }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            Obra {obra.numero}
            {obra.cliente ? <span style={{ fontWeight: 500, color: "#475569" }}> · {obra.cliente}</span> : null}
          </div>
          <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
            🔨 {obra.carpintero || "Sin carpintero"}{obra.nombreObra ? ` · ${obra.nombreObra}` : ""}
          </div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
          background: cfg.headerBg, color: cfg.color,
          padding: "3px 10px", borderRadius: 999,
        }}>
          {cfg.icon} {cfg.label}
        </span>
      </div>

      <div>
        <ProgresoBar obra={obra} alto={6} />
        <div style={{ fontSize: 10.5, color: atrasada ? "#c2410c" : "#94a3b8", marginTop: 3, fontWeight: atrasada ? 700 : 400 }}>
          {completos} de {total} pasos{atrasada ? " · atrasada ⚠️" : ""}
        </div>
      </div>

      {/* Cobranza: cuánto falta cobrar (lo que más se consulta de un vistazo) */}
      {(() => {
        const presupuesto = Number(obra.montoPresupuesto) || 0;
        if (presupuesto <= 0) return null;
        const cobrado = Number(obra.montoCobrado) || 0;
        const falta = Math.max(0, presupuesto - cobrado);
        const saldado = falta < 0.01;
        const moneda = obra.monedaPresupuesto || "$";
        const money = (v) => `${moneda} ${Number(v).toLocaleString("es-UY", { minimumFractionDigits: 2 })}`;
        const pct = Math.min(100, Math.round((cobrado / presupuesto) * 100));
        return (
          <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 11.5, color: "#64748b" }}>
                {saldado ? "Cobrado" : "Falta cobrar"}
              </span>
              <b style={{ fontSize: 13, color: saldado ? "#15803d" : "#c2410c" }}>
                {saldado ? money(cobrado) : money(falta)}
              </b>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: "#f1f5f9", marginTop: 4, overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: saldado ? "#16a34a" : "#f59e0b" }} />
            </div>
            <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 3 }}>
              {saldado ? "saldada" : `${money(cobrado)} de ${money(presupuesto)} · ${pct}%`}
            </div>
          </div>
        );
      })()}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, borderTop: "1px solid #f1f5f9", paddingTop: 8 }}>
        <AvatarResp resp={resp} />
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {dias != null && cfg.id !== "FINALIZADA" && (
            <span style={{ fontSize: 11.5, color: atrasada ? "#c2410c" : "#94a3b8", fontWeight: atrasada ? 700 : 400 }}>
              {dias === 0 ? "hoy" : `${dias} día${dias === 1 ? "" : "s"}`}
            </span>
          )}
          {meToca && (
            <span style={{
              fontSize: 9.5, fontWeight: 700, background: "#fef3c7", color: "#92400e",
              borderRadius: 999, padding: "2px 8px", textTransform: "uppercase", letterSpacing: 0.3,
            }}>
              Pendiente
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Modal nueva obra
// ─────────────────────────────────────────────────────────────

function NuevaObraModal({ onClose, onCreated, user, numeroSugerido }) {
  const [numero, setNumero] = useState(numeroSugerido ? String(numeroSugerido) : "");
  const [clienteSel, setClienteSel] = useState(null); // { codigo, nombre } del maestro
  const [qCliente, setQCliente] = useState("");
  const [clientes, setClientes] = useState([]);
  const [nombreObra, setNombreObra] = useState("");
  const [carpintero, setCarpintero] = useState("");
  const [notas, setNotas] = useState("");
  const [archivoPres, setArchivoPres] = useState(null); // File — presupuesto (obligatorio)
  const [montoManual, setMontoManual] = useState("");    // monto del presupuesto (sin IVA) — se carga a mano
  const [monedaManual, setMonedaManual] = useState("USD");
  const [prefillAuto, setPrefillAuto] = useState(false); // se prellenó con la lectura del archivo
  const [leyendoMonto, setLeyendoMonto] = useState(false);
  const [montoSena, setMontoSena] = useState("");        // seña pagada por el cliente
  const [archivoSena, setArchivoSena] = useState(null);  // comprobante de la seña (obligatorio)
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const senaRef = useRef(null);

  async function handleArchivoPres(file) {
    setArchivoPres(file);
    setPrefillAuto(false);
    if (!file) return;
    // La lectura del PDF es SOLO una sugerencia: el monto se controla a mano.
    setLeyendoMonto(true);
    try {
      const { extraerPresupuestoDeFile } = await import("../services/parseArchivosObra");
      const r = await extraerPresupuestoDeFile(file);
      if (r?.monto > 0 && !montoManual) {
        setMontoManual(String(r.monto));
        if (r.moneda) setMonedaManual(r.moneda);
        setPrefillAuto(true);
      }
    } catch { /* si no se puede leer, se carga a mano igual */ }
    finally { setLeyendoMonto(false); }
  }

  useEffect(() => {
    getClientesLista().then(setClientes).catch(err => console.warn("[clientes]", err));
  }, []);

  const clientesFiltrados = useMemo(() => {
    const t = qCliente.trim().toLowerCase();
    if (t.length < 2) return [];
    return clientes
      .filter(c => c.nombre.toLowerCase().includes(t) || String(c.codigo).includes(t))
      .slice(0, 8);
  }, [clientes, qCliente]);

  async function handleCrear(e) {
    e.preventDefault();
    setError("");
    if (!clienteSel) { setError("Elegí el cliente del maestro de Finnegans"); return; }
    if (!archivoPres) { setError("Adjuntá el presupuesto de OP"); return; }
    if (!(Number(montoManual) > 0)) { setError("Ingresá el monto del presupuesto (sin IVA)"); return; }
    // La seña es OPCIONAL para todos: si se carga, se registra como la primera
    // cobranza; si no, se agrega después desde la ficha (Cobranza).
    setBusy(true);
    try {
      const { id } = await crearObra(
        {
          numero,
          cliente: clienteSel.nombre,
          clienteCodigo: clienteSel.codigo,
          nombreObra,
          carpintero: carpintero || null,
          notas,
          montoPresupuesto: Number(montoManual),
          monedaPresupuesto: monedaManual,
        },
        user
      );
      // El presupuesto es el paso 1 del proceso
      try {
        await subirArchivoObra(id, "presupuesto", archivoPres, user);
      } catch (errUp) {
        alert("La obra se creó, pero falló la subida del presupuesto — adjuntalo desde la ficha: " + (errUp.message || errUp));
      }
      // La seña es opcional. Si cargaron un monto, se registra como la primera
      // cobranza; el comprobante se adjunta si lo subieron (si no, queda para la ficha).
      if (Number(montoSena) > 0) {
        try {
          const cobId = await agregarCobranza(id, { monto: Number(montoSena) }, user);
          if (archivoSena) await adjuntarDocCobranza(id, cobId, "comprobante", archivoSena, user);
        } catch (errSena) {
          alert("La obra se creó, pero falló el registro de la seña — cargala desde la ficha (Cobranza): " + (errSena.message || errSena));
        }
      }
      onCreated?.(id);
    } catch (err) {
      setError(err.message || "No se pudo crear la obra");
      setBusy(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "9px 12px", fontSize: 14,
    border: "1px solid #cbd5e1", borderRadius: 8, boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 12.5, fontWeight: 600, color: "#475569", display: "block", marginBottom: 4 };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 260, padding: 16,
        overflowY: "auto",
      }}
    >
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={handleCrear}
        style={{ position: "relative", background: "#fff", borderRadius: 14, padding: 20, width: "100%", maxWidth: 440, margin: "auto" }}
      >
        {busy && (
          <div className="obra-creando-overlay">
            <span className="spinner-inline spinner-inline--dark" style={{ width: 28, height: 28, borderWidth: 3, margin: 0 }} />
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1d4ed8" }}>Creando la obra…</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>Subiendo el presupuesto y el comprobante</div>
          </div>
        )}
        <h2 style={{ margin: "0 0 14px", fontSize: 18 }}>➕ Nueva obra PVC</h2>

        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={labelStyle}>Número de obra *</label>
            <input
              style={inputStyle} type="number" min="1" value={numero} required
              placeholder="205" onChange={e => setNumero(e.target.value)}
            />
            {numeroSugerido && String(numeroSugerido) === numero && (
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 3 }}>
                Sugerido automáticamente (última obra + 1) — podés cambiarlo
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Cliente (del maestro de Finnegans) *</label>
            {clienteSel ? (
              <div style={{ fontSize: 13.5, padding: "8px 10px", background: "#f0fdf4", borderRadius: 8 }}>
                ✓ <b>{clienteSel.nombre}</b> · código {clienteSel.codigo}
                <button
                  type="button" className="btn btn--ghost"
                  style={{ fontSize: 11.5, padding: "1px 8px", marginLeft: 6 }}
                  onClick={() => setClienteSel(null)}
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <>
                <input
                  style={inputStyle} value={qCliente} placeholder="Buscar por nombre o código…"
                  onChange={e => setQCliente(e.target.value)}
                />
                {clientesFiltrados.map(c => (
                  <div
                    key={c.codigo}
                    onClick={() => { setClienteSel(c); setQCliente(""); }}
                    style={{ fontSize: 13, padding: "6px 8px", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                  >
                    {c.nombre} <span style={{ color: "#94a3b8" }}>· {c.codigo}</span>
                  </div>
                ))}
              </>
            )}
          </div>
          <div>
            <label style={labelStyle}>Presupuesto de OP (aceptado por el cliente) *</label>
            <input
              ref={fileRef} type="file" accept=".doc,.docx,.pdf,.xls,.xlsx"
              style={{ display: "none" }}
              onChange={e => handleArchivoPres(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              className={archivoPres ? "btn btn--ghost" : "btn btn--outline"}
              style={{ fontSize: 13 }}
              onClick={() => fileRef.current?.click()}
            >
              {archivoPres ? `✓ ${archivoPres.name}` : "📎 Adjuntar presupuesto"}
            </button>
            {leyendoMonto && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>Leyendo el monto…</div>}
          </div>
          <div>
            <label style={labelStyle}>Monto del presupuesto (sin IVA) *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                style={{ ...inputStyle, width: 90, flex: "0 0 auto" }}
                value={monedaManual}
                onChange={e => setMonedaManual(e.target.value)}
              >
                <option value="USD">USD</option>
                <option value="UYU">UYU</option>
                <option value="EUR">EUR</option>
              </select>
              <input
                style={{ ...inputStyle, flex: 1 }} type="number" step="0.01" min="0"
                value={montoManual} placeholder="Ej: 5000.00"
                onChange={e => { setMontoManual(e.target.value); setPrefillAuto(false); }}
              />
            </div>
            {prefillAuto && !!montoManual && (
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>
                Sugerido a partir del archivo. Verificalo y corregilo si hace falta.
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Seña — pago del cliente (opcional)</label>
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 6 }}>
              Si el cliente ya pagó la seña, cargala y se registra como la primera cobranza.
              Si no, podés agregarla después desde la ficha (Cobranza).
            </div>
            <input
              style={inputStyle} type="number" step="0.01" min="0" value={montoSena}
              placeholder="Monto de la seña" onChange={e => setMontoSena(e.target.value)}
            />
            <input
              ref={senaRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
              style={{ display: "none" }}
              onChange={e => setArchivoSena(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              className={archivoSena ? "btn btn--ghost" : "btn btn--outline"}
              style={{ fontSize: 13, marginTop: 6 }}
              onClick={() => senaRef.current?.click()}
            >
              {archivoSena ? `✓ ${archivoSena.name}` : "📎 Adjuntar comprobante de seña"}
            </button>
          </div>
          <div>
            <label style={labelStyle}>Nombre / dirección de la obra</label>
            <input
              style={inputStyle} value={nombreObra} placeholder="Casa Punta Gorda"
              onChange={e => setNombreObra(e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Carpintero</label>
            <select style={inputStyle} value={carpintero} onChange={e => setCarpintero(e.target.value)}>
              <option value="">Sin asignar</option>
              {CARPINTEROS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Notas</label>
            <textarea
              style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notas}
              onChange={e => setNotas(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 10, fontSize: 13, color: "#b91c1c", background: "#fee2e2", padding: "8px 10px", borderRadius: 8 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button type="submit" className="btn" disabled={busy || !numero}>
            {busy ? <><span className="spinner-inline" />Creando obra…</> : "Crear obra"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Página
// ─────────────────────────────────────────────────────────────

export default function ObrasTablero() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("ACTIVAS"); // ACTIVAS | TODAS | ATRASADAS | <estadoId>
  const [busqueda, setBusqueda] = useState("");
  const [showNueva, setShowNueva] = useState(false);
  const [showCostos, setShowCostos] = useState(false);
  const [showProductos, setShowProductos] = useState(false);
  const [showClientes, setShowClientes] = useState(false);

  useEffect(() => {
    const off = listenObras(
      (list) => { setObras(list); setLoading(false); },
      () => setLoading(false)
    );
    return off;
  }, []);

  // Acciones pendientes del usuario actual, obra por obra ("tu trabajo")
  const misAcciones = useMemo(() => {
    const out = [];
    for (const o of obras) {
      for (const a of accionesPendientesPara(o, profile)) {
        out.push({ obra: o, ...a });
      }
    }
    return out;
  }, [obras, profile]);

  const misPendientesIds = useMemo(
    () => new Set(misAcciones.map(a => a.obra.id)),
    [misAcciones]
  );

  // Métricas del tablero
  const stats = useMemo(() => {
    let esperandoCompra = 0, enCarpinteria = 0;
    for (const o of obras) {
      const h = hitosObra(o);
      const fase = faseObra(o);
      if (obraConGuale(o) && h.pedido && !h.facturaCompra && fase.id !== "FINALIZADA") esperandoCompra++;
      if (fase.id === "EN_CARPINTERIA") enCarpinteria++;
    }
    return { esperandoCompra, enCarpinteria };
  }, [obras]);

  const conteos = useMemo(() => {
    const c = { TODAS: obras.length, ACTIVAS: 0, ATRASADAS: 0, MIAS: misPendientesIds.size };
    for (const f of FASES_OBRA) c[f.id] = 0;
    for (const o of obras) {
      const fase = faseObra(o);
      c[fase.id]++;
      if (fase.id !== "FINALIZADA") c.ACTIVAS++;
      if (estaAtrasada(o)) c.ATRASADAS++;
    }
    return c;
  }, [obras, misPendientesIds]);

  const visibles = useMemo(() => {
    let list = obras;
    if (filtro === "ACTIVAS") list = list.filter(o => faseObra(o).id !== "FINALIZADA");
    else if (filtro === "ATRASADAS") list = list.filter(estaAtrasada);
    else if (filtro === "MIAS") list = list.filter(o => misPendientesIds.has(o.id));
    else if (filtro !== "TODAS") list = list.filter(o => faseObra(o).id === filtro);

    const q = busqueda.trim().toLowerCase();
    if (q) {
      list = list.filter(o =>
        String(o.numero).includes(q) ||
        (o.cliente || "").toLowerCase().includes(q) ||
        (o.nombreObra || "").toLowerCase().includes(q) ||
        (o.carpintero || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [obras, filtro, busqueda, misPendientesIds]);

  const chipStyle = (activo, color, bg) => ({
    fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    padding: "5px 12px", borderRadius: 999, border: "1px solid",
    borderColor: activo ? (color || "#0f172a") : "#e2e8f0",
    background: activo ? (bg || "#f1f5f9") : "#fff",
    color: color || "#475569",
  });

  const puedeCrear = ["obras", "admin"].includes(
    String(profile?.rol || profile?.role || "").toLowerCase()
  );
  // Actualizar las bases maestras (productos y clientes) = SOLO admin
  const esAdmin = permisosObras(profile).admin;

  return (
    <div style={{ padding: "16px", maxWidth: 1280, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            className="btn btn--outline"
            onClick={() => navigate("/")}
            title="Volver al menú principal"
          >
            ← Menú
          </button>
          <h1 style={{ margin: 0, fontSize: 22 }}>🪟 Obras PVC</h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {esAdmin && (
            <button className="btn btn--outline" onClick={() => setShowProductos(true)}>
              📦 Productos
            </button>
          )}
          {esAdmin && (
            <button className="btn btn--outline" onClick={() => setShowClientes(true)}>
              👥 Clientes
            </button>
          )}
          {permisosObras(profile).finanzas && (
            <button className="btn btn--outline" onClick={() => setShowCostos(true)}>
              💲 Costos
            </button>
          )}
          {puedeCrear && (
            <button className="btn" onClick={() => setShowNueva(true)}>
              ➕ Nueva obra
            </button>
          )}
        </div>
      </div>

      {/* ── Métricas rápidas ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, margin: "12px 0" }}>
        {[
          { label: "En curso", valor: conteos.ACTIVAS },
          { label: "Esperando compra", valor: stats.esperandoCompra },
          { label: "En carpintería", valor: stats.enCarpinteria },
          { label: "Atrasadas", valor: conteos.ATRASADAS, alerta: conteos.ATRASADAS > 0 },
        ].map(m => (
          <div key={m.label} style={{
            background: m.alerta ? "#fee2e2" : "#fff",
            border: `1px solid ${m.alerta ? "#fca5a5" : "#e2e8f0"}`,
            borderRadius: 10, padding: "8px 12px",
          }}>
            <div style={{ fontSize: 11, color: m.alerta ? "#b91c1c" : "#64748b" }}>{m.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: m.alerta ? "#b91c1c" : "#0f172a" }}>{m.valor}</div>
          </div>
        ))}
      </div>

      {/* ── Tu trabajo ── */}
      {misAcciones.length > 0 && (
        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 12, padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
            Tareas pendientes{profile?.nombre ? ` de ${profile.nombre}` : ""} ({misAcciones.length})
          </div>
          {misAcciones.slice(0, 5).map((a, i) => (
            <div key={a.obra.id + a.hito} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
              padding: "5px 0", borderBottom: i < Math.min(misAcciones.length, 5) - 1 ? "1px solid #fde68a" : "none",
            }}>
              <span style={{ fontSize: 12.5, color: "#92400e" }}>
                <b>Obra {a.obra.numero}</b>{a.obra.cliente ? ` · ${a.obra.cliente}` : ""} — {a.label}
              </span>
              <button
                className="btn btn--outline"
                style={{ fontSize: 11.5, padding: "2px 10px", whiteSpace: "nowrap" }}
                onClick={() => navigate(`/obras/${encodeURIComponent(a.obra.id)}`)}
              >
                Ir →
              </button>
            </div>
          ))}
          {misAcciones.length > 5 && (
            <div style={{ fontSize: 11.5, color: "#b45309", marginTop: 4, cursor: "pointer" }} onClick={() => setFiltro("MIAS")}>
              … y {misAcciones.length - 5} más — ver todas
            </div>
          )}
        </div>
      )}

      <input
        value={busqueda}
        onChange={e => setBusqueda(e.target.value)}
        placeholder="Buscar por número, cliente o carpintero…"
        style={{
          width: "100%", boxSizing: "border-box", margin: "0 0 10px",
          padding: "9px 12px", fontSize: 14, border: "1px solid #cbd5e1", borderRadius: 8,
        }}
      />

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {conteos.MIAS > 0 && (
          <span style={chipStyle(filtro === "MIAS", "#92400e", "#fef3c7")} onClick={() => setFiltro(filtro === "MIAS" ? "ACTIVAS" : "MIAS")}>
            Mis tareas · {conteos.MIAS}
          </span>
        )}
        <span style={chipStyle(filtro === "ACTIVAS")} onClick={() => setFiltro("ACTIVAS")}>
          En curso · {conteos.ACTIVAS}
        </span>
        {conteos.ATRASADAS > 0 && (
          <span style={chipStyle(filtro === "ATRASADAS", "#c2410c", "#ffedd5")} onClick={() => setFiltro("ATRASADAS")}>
            ⚠️ Atrasadas · {conteos.ATRASADAS}
          </span>
        )}
        {FASES_OBRA.filter(f => conteos[f.id] > 0).map(f => (
          <span
            key={f.id}
            style={chipStyle(filtro === f.id, f.color, f.headerBg)}
            onClick={() => setFiltro(filtro === f.id ? "ACTIVAS" : f.id)}
          >
            {f.icon} {f.label} · {conteos[f.id]}
          </span>
        ))}
        <span style={chipStyle(filtro === "TODAS")} onClick={() => setFiltro("TODAS")}>
          Todas · {conteos.TODAS}
        </span>
      </div>

      {loading ? (
        <div style={{ color: "#64748b", padding: 30, textAlign: "center" }}>Cargando obras…</div>
      ) : visibles.length === 0 ? (
        <div style={{
          color: "#64748b", padding: "40px 20px", textAlign: "center",
          background: "#f8fafc", borderRadius: 12, border: "1px dashed #cbd5e1",
        }}>
          {obras.length === 0
            ? "Todavía no hay obras cargadas. Creá la primera con “Nueva obra”."
            : "No hay obras que coincidan con el filtro."}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))", gap: 14 }}>
          {visibles.map(o => <ObraCard key={o.id} obra={o} meToca={misPendientesIds.has(o.id)} />)}
        </div>
      )}

      {showProductos && (
        <ProductosModal
          user={{ uid: user?.uid, nombre: profile?.nombre, displayName: user?.displayName, email: user?.email }}
          onClose={() => setShowProductos(false)}
        />
      )}

      {showClientes && (
        <ClientesModal
          user={{ uid: user?.uid, nombre: profile?.nombre, displayName: user?.displayName, email: user?.email }}
          onClose={() => setShowClientes(false)}
        />
      )}

      {showCostos && (
        <CostosModal
          user={{ uid: user?.uid, nombre: profile?.nombre, displayName: user?.displayName, email: user?.email }}
          onClose={() => setShowCostos(false)}
        />
      )}

      {showNueva && (
        <NuevaObraModal
          user={{ uid: user?.uid, nombre: profile?.nombre, displayName: user?.displayName, email: user?.email }}
          numeroSugerido={obras.length ? Math.max(...obras.map(o => Number(o.numero) || 0)) + 1 : null}
          onClose={() => setShowNueva(false)}
          onCreated={(id) => navigate(`/obras/${encodeURIComponent(id)}`)}
        />
      )}
    </div>
  );
}
